import { useEffect, useMemo, useState } from "react";
import { useRecoilValue } from "recoil";

import { useTrackingActions } from "@ryde/redux";
import {
  IRideInvestigationDto,
  RideInvestigationApi,
  rideTrackingRenderAF,
  useDebugMode,
} from "@ryde/shared";

import { IPlaybackModel } from "./investigation.models";
import { buildPlaybackModel } from "./investigation.utils";

interface IUseRideInvestigationResult {
  payload?: IRideInvestigationDto;
  model?: IPlaybackModel;
  loading: boolean;
  error: boolean;
}

/**
 * Loads the investigation payload for a ride and makes sure its GPS trail is in Recoil
 * (rideTrackingRenderAF), then builds the preprocessed playback model. The model rebuilds when the
 * trail arrives, so playback starts from the semantic layer and gains the real path a moment later.
 */
export const useRideInvestigation = (
  rideId?: number,
): IUseRideInvestigationResult => {
  const { debugMode, debugModeLevel } = useDebugMode();
  const { fetchRideTrackings } = useTrackingActions();
  const renderState = useRecoilValue(rideTrackingRenderAF(rideId ?? 0));
  const [payload, setPayload] = useState<IRideInvestigationDto | undefined>(
    undefined,
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!rideId) {
      setPayload(undefined);
      setError(false);
      setLoading(false);

      return;
    }

    const abortController = new AbortController();
    let cancelled = false;

    setLoading(true);
    setError(false);

    const loadInvestigation = async (): Promise<void> => {
      // Populate rideTrackingRenderAF(rideId) in the background so the trail and playback share one source.
      fetchRideTrackings(rideId, {
        debugMode,
        backgroundAction: true,
        abortController,
      }).catch(() => undefined);

      try {
        const result = await RideInvestigationApi.fetchInvestigation(rideId, {
          debugMode: debugModeLevel,
          abortController,
        });

        if (cancelled) {
          return;
        }

        if (result?.success && result.data) {
          setPayload(result.data);
        } else {
          setPayload(undefined);
          setError(true);
        }
      } catch {
        if (!cancelled) {
          setError(true);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void loadInvestigation();

    return () => {
      cancelled = true;
      abortController.abort();
    };
  }, [rideId, debugMode, debugModeLevel, fetchRideTrackings]);

  const model = useMemo(
    () => (payload ? buildPlaybackModel({ payload, renderState }) : undefined),
    // Rebuild only when the payload or the trail genuinely changes (version bumps on real appends) —
    // not on every fresh renderState object reference produced by polling.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [payload, renderState?.version],
  );

  return { payload, model, loading, error };
};
